import { Component } from '@theme/component';

// A compact colour + spatial-outline descriptor. No uploaded image leaves this browser.
export function photoDescriptor(rgba, size = 32) {
  const histogram = new Array(36).fill(0);
  const cells = new Array(48).fill(0);
  const counts = new Array(16).fill(0);
  const edges = new Array(16).fill(0);
  let weight = 0;
  for (let y = 0; y < size; y++) for (let x = 0; x < size; x++) {
    const at = (y * size + x) * 4;
    const r = rgba[at] / 255, g = rgba[at + 1] / 255, b = rgba[at + 2] / 255;
    if (rgba[at + 3] < 32 || Math.min(r, g, b) > .94) continue;
    const high = Math.max(r, g, b), low = Math.min(r, g, b), delta = high - low;
    const saturation = high === 0 ? 0 : delta / high;
    let hue = 0;
    if (delta) hue = (high === r ? (g - b) / delta + (g < b ? 6 : 0) : high === g ? (b - r) / delta + 2 : (r - g) / delta + 4) / 6;
    const w = .65 + .35 * (1 - Math.abs((x / size) * 2 - 1));
    const chroma = Math.max(0, Math.min(1, (saturation - .05) / .3));
    const light = Math.max(0, Math.min(1, (high - .15) / .65));
    const hueAt = hue * 12, hueLow = Math.floor(hueAt) % 12, hueMix = hueAt - Math.floor(hueAt);
    for (const [hueBin, hueWeight] of [[hueLow, 1 - hueMix], [(hueLow + 1) % 12, hueMix]]) {
      histogram[hueBin] += w * chroma * hueWeight * (1 - light);
      histogram[hueBin + 12] += w * chroma * hueWeight * light;
    }
    const greyAt = high * 11, greyLow = Math.floor(greyAt), greyMix = greyAt - greyLow;
    histogram[24 + greyLow] += w * (1 - chroma) * (1 - greyMix);
    histogram[24 + Math.min(11, greyLow + 1)] += w * (1 - chroma) * greyMix;
    weight += w;
    const cell = Math.min(3, Math.floor(y * 4 / size)) * 4 + Math.min(3, Math.floor(x * 4 / size));
    cells[cell * 3] += r;cells[cell * 3 + 1] += g;cells[cell * 3 + 2] += b;counts[cell]++;
    if (x && y) {
      const left = at - 4, above = at - size * 4;
      edges[cell] += (Math.abs(r - rgba[left] / 255) + Math.abs(g - rgba[left + 1] / 255) + Math.abs(b - rgba[left + 2] / 255) + Math.abs(r - rgba[above] / 255)) / 4;
    }
  }
  for (let i = 0; i < cells.length; i++) cells[i] = counts[Math.floor(i / 3)] ? cells[i] / counts[Math.floor(i / 3)] : 1;
  return { histogram: histogram.map(v => v / (weight || 1)), cells, edges: edges.map((v, i) => v / (counts[i] || 1)) };
}

export function photoDistance(a, b) {
  const colour = 1 - a.histogram.reduce((n, v, i) => n + Math.sqrt(v * b.histogram[i]), 0);
  const spatial = a.cells.reduce((n, v, i) => n + Math.abs(v - b.cells[i]), 0) / a.cells.length;
  const outline = a.edges.reduce((n, v, i) => n + Math.abs(v - b.edges[i]), 0) / a.edges.length;
  return colour * .65 + spatial * .25 + outline * .1;
}

class MTQPhotoSearch extends Component {
  controller;
  request;
  previewURL;
  uploadedImage;
  opener;
  catalogueCache = new Map();
  signatureCache = new Map();
  connectedCallback() { super.connectedCallback();this.initialize(); }
  updatedCallback() { super.updatedCallback();this.initialize(); }
  disconnectedCallback() { super.disconnectedCallback();this.cleanup(); }
  cleanup() { this.controller?.abort();this.request?.abort();if (this.previewURL) URL.revokeObjectURL(this.previewURL); }
  initialize() {
    this.controller?.abort();this.controller = new AbortController();
    const signal = this.controller.signal;
    this.dialog = this.querySelector('dialog');
    this.labels = JSON.parse(this.querySelector('[data-photo-labels]').textContent);
    document.addEventListener('click', event => {
      if (!(event.target instanceof Element) || !event.target.closest('[data-mtq-photo-open]')) return;
      event.preventDefault();this.opener = event.target.closest('[data-mtq-photo-open]');this.dialog.showModal();
    }, { signal });
    this.querySelector('[data-photo-close]').addEventListener('click', () => this.dialog.close(), { signal });
    this.dialog.addEventListener('close', () => { this.request?.abort();this.opener?.focus(); }, { signal });
    this.querySelector('[data-photo-choose]').addEventListener('click', () => this.querySelector('[data-photo-file]').click(), { signal });
    this.querySelector('[data-photo-camera]').addEventListener('click', () => this.querySelector('[data-photo-capture]').click(), { signal });
    this.querySelectorAll('input[type=file]').forEach(input => input.addEventListener('change', () => this.choose(input.files?.[0]), { signal }));
    this.querySelector('[data-photo-search]').addEventListener('click', () => this.search(), { signal });
    const drop = this.querySelector('[data-photo-drop]');
    drop.addEventListener('dragover', event => event.preventDefault(), { signal });
    drop.addEventListener('drop', event => { event.preventDefault();this.choose(event.dataTransfer?.files?.[0]); }, { signal });
  }
  status(text) { this.querySelector('[data-photo-status]').textContent = text; }
  async image(source, crossOrigin = false) {
    return new Promise((resolve, reject) => {
      const image = new Image();if (crossOrigin) image.crossOrigin = 'anonymous';
      image.onload = () => resolve(image);image.onerror = reject;image.src = source;
    });
  }
  descriptor(image) {
    const canvas = document.createElement('canvas');canvas.width = canvas.height = 32;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.fillStyle = '#fff';ctx.fillRect(0, 0, 32, 32);
    const scale = Math.min(32 / image.naturalWidth, 32 / image.naturalHeight);
    const w = image.naturalWidth * scale, h = image.naturalHeight * scale;
    ctx.drawImage(image, (32 - w) / 2, (32 - h) / 2, w, h);
    return photoDescriptor(ctx.getImageData(0, 0, 32, 32).data);
  }
  async choose(file) {
    if (!file) return;
    this.request?.abort();this.querySelector('[data-photo-results]').replaceChildren();
    const button = this.querySelector('[data-photo-search]');button.disabled = true;
    if (!file.type.startsWith('image/') || file.size > 12 * 1024 * 1024) { this.status(this.labels.invalid);return; }
    try {
      if (this.previewURL) URL.revokeObjectURL(this.previewURL);
      this.previewURL = URL.createObjectURL(file);this.uploadedImage = await this.image(this.previewURL);
      const preview = this.querySelector('[data-photo-preview]');preview.src = this.previewURL;preview.hidden = false;
      this.status(this.labels.ready);button.disabled = false;
    } catch { this.status(this.labels.invalid); }
  }
  async catalogue(handle, signal) {
    const cached = this.catalogueCache.get(handle);
    if (cached && Date.now() - cached.at < 300000) return cached.products;
    const products = [], root = this.dataset.root || '/';
    for (let page = 1; page <= 20; page++) {
      const url = new URL(`${root}collections/${encodeURIComponent(handle)}/products.json`, location.origin);
      url.searchParams.set('limit', '250');url.searchParams.set('page', String(page));
      const response = await fetch(url, { signal });if (!response.ok) throw new Error('Catalogue unavailable');
      const data = await response.json();if (!Array.isArray(data.products)) throw new Error('Invalid catalogue');
      products.push(...data.products);if (data.products.length < 250) break;
    }
    this.catalogueCache.set(handle, { products, at: Date.now() });return products;
  }
  async search() {
    if (!this.uploadedImage) return;
    this.request?.abort();this.request = new AbortController();const signal = this.request.signal;
    const button = this.querySelector('[data-photo-search]');button.disabled = true;
    const grid = this.querySelector('[data-photo-results]');grid.replaceChildren();grid.setAttribute('aria-busy', 'true');
    this.status(this.labels.scanning);
    try {
      const target = this.descriptor(this.uploadedImage);
      const catalogue = await this.catalogue(this.querySelector('[data-photo-category]').value, signal);
      const queue = [...new Map(catalogue.filter(p => p.images?.[0]?.src).map(p => [p.handle, p])).values()];
      const ranked = [];
      await Promise.all(Array.from({ length: Math.min(4, queue.length) }, async () => {
        while (queue.length && !signal.aborted) {
          const product = queue.shift();const url = new URL(product.images[0].src, location.origin);url.searchParams.set('width', '160');
          try {
            let signature = this.signatureCache.get(url.href);
            if (!signature) { signature = this.descriptor(await this.image(url.href, true));this.signatureCache.set(url.href, signature); }
            ranked.push({ product, distance: photoDistance(target, signature) });
          } catch { /* An unavailable or CORS-restricted image is not a visual match. */ }
        }
      }));
      if (signal.aborted || !this.isConnected) return;
      ranked.sort((a, b) => a.distance - b.distance);
      const matches = ranked.filter(row => row.distance < .68).slice(0, 18);
      for (const item of matches) grid.append(this.card(item.product));
      this.dataset.matchCount = String(matches.length);this.dataset.scanned = String(ranked.length);
      this.status(matches.length ? this.labels.matches : this.labels.empty);
    } catch (error) { if (error.name !== 'AbortError') this.status(this.labels.error); }
    finally { if (this.request.signal === signal) { button.disabled = false;grid.setAttribute('aria-busy', 'false'); } }
  }
  card(product) {
    const root = this.dataset.root || '/', url = `${root}products/${encodeURIComponent(product.handle)}`;
    const card = document.createElement('article');card.className = 'mtq-market-card';
    const picture = document.createElement('a');picture.href = url;picture.className = 'mtq-market-card__image';picture.setAttribute('aria-label', product.title);
    const image = document.createElement('img');image.src = product.images[0].src;image.alt = product.title;image.width = image.height = 300;image.loading = 'lazy';picture.append(image);
    const body = document.createElement('div');body.className = 'mtq-market-card__body';
    const title = document.createElement('h3'), link = document.createElement('a');link.href = url;link.textContent = product.title;title.append(link);
    const price = document.createElement('p');price.className = 'mtq-market-card__price';
    price.textContent = new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: this.dataset.currency }).format(Math.min(...product.variants.map(v => Number(v.price))));
    body.append(title, price);card.append(picture, body);return card;
  }
}
if (!customElements.get('mtq-photo-search')) customElements.define('mtq-photo-search', MTQPhotoSearch);
